// Inbox slot — hot-lead override banner (US-088, EPIC-07).
//
// Mounted by `<ThreadPanel>` (5c.2) when `derivePolicy(...)` from
// `lib/modePolicy.ts` returns `flags.showHotOverride === true`. That happens
// when:
//   - `account.aiMode === 'auto'` (account default is auto-send), AND
//   - `conversation.isVeryHot === true` (AI scored this conv "very hot"), AND
//   - `voice.notifyHotAction === 'validate'` (user opted into hot-lead review).
//
// Per the spec the override is purely derived (no per-conversation flag is
// stored — the trio above is the source of truth). The banner is a passive
// indicator: the actual mode swap to `review` is handled by the resolver in
// `@barel/core`, and the thread renders the review-mode draft editor in
// place of the auto-preview.
//
// Design ref: `prototypes_and_docs/ls-voice.jsx:653-666` (notify settings)
//          + `prototypes_and_docs/ls-inbox.jsx:1126-1133` (override logic).

import { useT } from '@barel/i18n';
import { Banner } from '@barel/ui';

export interface HotOverrideProps {
  /**
   * When `false`, the component renders nothing — kept as a prop so the parent
   * `ThreadPanel` can lift visibility into the policy decision without remount
   * churn between conv switches.
   */
  visible: boolean;
}

export function HotOverride({ visible }: HotOverrideProps) {
  const t = useT('inbox');
  if (!visible) return null;
  return (
    <Banner
      data-component="HotOverride"
      data-testid="inbox-hot-override-banner"
      tone="orange"
      title={t('policy.hotOverride.title')}
      icon="🔥"
    >
      {t('policy.hotOverride.body')}
    </Banner>
  );
}
